import * as React from 'react'
import { FlatList, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Select from '@components/select/Select'
import Modal from '@components/modal/Modal'
import Drawer from '@components/drawer/Drawer'
import Icon from '@components/icon/Icons'
import TextInput from '@components/textInput/TextInput'
import Button from '@components/button/Button'

import sanitize from '@utils/Sanitizer'
import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

interface Option {
  value: string
  label: string
}

interface Props {
  children: React.ReactNode
  title: string
  subtitle: string
  data: Option[]
  value?: string
  onItemSelected: (item: string) => void
}

const SearchModal = ({ data, onItemSelected, value, subtitle, title }: Omit<Props, 'children'>) => {
  const i18n = useI18n()

  const [search, setSearch] = React.useState('')
  const [selected, setSelected] = React.useState(value)
  const [, bp] = useBreakpoint()

  const items = data.filter((d) => sanitize(d.label).includes(sanitize(search)))
  const last = data.find((d) => d.value === value)

  const onSubmit = () => {
    onItemSelected(selected || '')
    Modal.close()
  }

  const renderItem = ({ item }: { item: Option }) => {
    const focused = item.value === selected

    return (
      <SelectContainer>
        <Select
          label={item.label}
          onClick={() => setSelected(item.value)}
          focused={focused}
          selectStyle={!focused ? 'empty' : 'default'}
        />
      </SelectContainer>
    )
  }

  return (
    <SearchContainer>
      <InfoContainer>
        <ModalTitle>{title}</ModalTitle>
        {!!last && <Current>{subtitle}</Current>}

        <Close activeOpacity={0.8} onPress={() => Modal.close()}>
          <Icon name="close" size={24} />
        </Close>
      </InfoContainer>

      <ListContainer>
        <InputContainer>
          <TextInput
            value={search}
            onChange={setSearch}
            placeholder={i18n.t('common.search')}
            left={{ name: 'search' }}
          />
        </InputContainer>

        <FlatList
          key={bp}
          data={items}
          renderItem={renderItem}
          keyExtractor={(item) => item.value}
          extraData={selected}
          ItemSeparatorComponent={ModalSeparator}
          numColumns={bp === 'phone' ? 1 : 2}
        />
      </ListContainer>

      <InfoContainer>
        <ButtonContainer>
          <Button label={i18n.t('common.validate')} status={!!selected ? 'active' : 'disabled'} onClick={onSubmit} />
        </ButtonContainer>
      </InfoContainer>
    </SearchContainer>
  )
}

const SearchDrawer = ({ data, onItemSelected, value, title }: Omit<Props, 'children'>) => {
  const [selected, setSelected] = React.useState(value)
  const [searchedText, setSearchedText] = React.useState('')
  const i18n = useI18n()

  const renderItem = ({ item }: { item: (typeof data)[number] }) => {
    const isSelected = selected === item.value
    return (
      <Cell onPress={() => setSelected(item.value)}>
        <Item>{item.label}</Item>
        {isSelected && <Icon name="check" color={theme.colors.primary} size={24} />}
      </Cell>
    )
  }

  const validate = () => {
    selected && onItemSelected(selected)
    Drawer.close()
  }

  const searchedData = React.useMemo(
    () => data.filter((d) => sanitize(d.label).includes(sanitize(searchedText))),
    [searchedText]
  )

  return (
    <MainContainer>
      <Header>
        <SmallHeader onPress={() => Drawer.close()}>
          <Icon name="close" size={35} color={theme.colors.primary} />
        </SmallHeader>
        <CenterHeader>
          <DrawerTitle>{title}</DrawerTitle>
        </CenterHeader>
        <SmallHeader />
      </Header>
      <TopContainer>
        <TextInput
          onChange={setSearchedText}
          searchStyle="onLight"
          left={{ name: 'search' }}
          placeholder={i18n.t('screens.incident.PRESTA.drawer.search')}
        />
        <DrawerTitle top={18}>{i18n.t('screens.incident.PRESTA.drawer.question')}</DrawerTitle>
      </TopContainer>
      <FlatList data={searchedData} renderItem={renderItem} ItemSeparatorComponent={DrawerSeparator} />
      <Footer>
        <Button
          label={i18n.t('screens.incident.PRESTA.drawer.validate')}
          onClick={validate}
          status={!!selected ? 'active' : 'disabled'}
        />
      </Footer>
    </MainContainer>
  )
}

const Search = ({ children, ...rest }: Props) => {
  const [, bp] = useBreakpoint()
  const openSearch = () => {
    if (bp !== 'phone') {
      Modal.open({
        Content: () => <SearchModal {...rest} />,
      })
    } else {
      Drawer.open({
        render: () => <SearchDrawer {...rest} />,
        hideTarget: true,
      })
    }
  }

  return (
    <TouchableOpacity activeOpacity={0.8} onPress={openSearch}>
      {children}
    </TouchableOpacity>
  )
}

export default Search

//MODAL
const SearchContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'width: 880px'};
`

const InfoContainer = styled(View)`
  padding: 24px;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  position: relative;
`

const ModalTitle = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  font-size: 24px;
  margin-bottom: 8px;
`

const Current = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
`

const Close = styled(TouchableOpacity)`
  position: absolute;
  top: ${(props) => (props.theme.breakpoint === 'phone' ? 4 : 24)}px;
  right: ${(props) => (props.theme.breakpoint === 'phone' ? 4 : 24)}px;
  padding: 5px;
`

const ListContainer = styled(View)`
  padding: 24px;
  height: ${(props) => (props.theme.breakpoint === 'phone' ? 325 : 400)}px;
  background-color: ${(props) => props.theme.colors.background};
`

const InputContainer = styled(View)`
  margin-bottom: 24px;
`

const SelectContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 400px;`}
  margin-right: 24px;
`

const ModalSeparator = styled(View)`
  height: 24px;
`

const ButtonContainer = styled(View)`
  align-self: center;
  width: 275px;
`

//DRAWER
const MainContainer = styled(View)`
  flex: 1;
  padding-bottom: ${(props) => props.theme.insets.bottom}px;
`
const TopContainer = styled(View)`
  padding: 0 21px 16px;
`
const Cell = styled(TouchableOpacity)`
  background-color: ${(props) => props.theme.colors.background};
  padding: 5px 16px;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`
const Item = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`
const DrawerSeparator = styled(View)`
  height: 1px;
  background-color: ${(props) => props.theme.colors.disable};
  margin: 0 16px;
`
const Footer = styled(View)`
  padding: 16px;
`
const Header = styled(View)`
  flex-direction: row;
  padding: 21px;
`
const SmallHeader = styled(TouchableOpacity)`
  flex: 1;
`
const CenterHeader = styled(View)`
  flex: 4;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
`
const DrawerTitle = styled(Text)<{ top?: number }>`
  ${(props) => props.theme.fonts.Headline};
  margin-top: ${(props) => (props.top ? props.top : 0)}px;
`
